/**
 * Fire Equipment Service
 * API integration for fire-fighting equipment (inventário NR 23) and its inspections.
 */
import apiClient from './apiClient';
import type {
  FireEquipment,
  FireEquipmentCreateData,
  FireEquipmentDetail,
  FireEquipmentFilters,
  FireEquipmentListResponse,
  FireEquipmentSummary,
  FireEquipmentTypes,
  FireEquipmentUpdateData,
  FireInspection,
  FireInspectionFormData,
  FireInspectionUpdateData,
} from '../types/fireEquipment';
import type { DependencyCheck } from '../types/dependencyCheck';

export * from '../types/fireEquipment';

type Id = string | number;

const FILTROS_DA_QUERY = ['companyId', 'departmentId', 'type', 'situation', 'search'] as const;

function montarQuery(filtros: FireEquipmentFilters): URLSearchParams {
  const { page = 1, limit = 20 } = filtros;
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });

  FILTROS_DA_QUERY.forEach((nome) => {
    const valor = filtros[nome];
    if (valor !== undefined && valor !== '') query.append(nome, String(valor));
  });

  return query;
}

class FireEquipmentService {
  private baseUrl = '/fire-equipments';

  async getFireEquipments(filtros: FireEquipmentFilters = {}): Promise<FireEquipmentListResponse> {
    const response = await apiClient.get(`${this.baseUrl}?${montarQuery(filtros).toString()}`);
    return response.data;
  }

  async getTypes(): Promise<FireEquipmentTypes> {
    const response = await apiClient.get(`${this.baseUrl}/types`);
    return response.data.data || response.data;
  }

  async getSummary(companyId?: Id, departmentId?: Id): Promise<FireEquipmentSummary> {
    const params = {
      ...(companyId ? { companyId } : {}),
      ...(departmentId ? { departmentId } : {}),
    };
    const response = await apiClient.get(`${this.baseUrl}/summary`, {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data.data || response.data;
  }

  async getFireEquipmentById(id: Id): Promise<FireEquipmentDetail> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  async createFireEquipment(data: FireEquipmentCreateData): Promise<FireEquipment> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  async updateFireEquipment(id: Id, data: FireEquipmentUpdateData): Promise<FireEquipment> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteFireEquipment(id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async checkDependencies(id: Id): Promise<DependencyCheck> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  async getInspections(equipmentId: Id): Promise<FireInspection[]> {
    const response = await apiClient.get(`${this.baseUrl}/${equipmentId}/inspections`);
    return response.data.data || response.data;
  }

  async createInspection(equipmentId: Id, data: FireInspectionFormData): Promise<FireInspection> {
    const response = await apiClient.post(`${this.baseUrl}/${equipmentId}/inspections`, data);
    return response.data.data || response.data;
  }

  async updateInspection(
    equipmentId: Id,
    inspectionId: Id,
    data: FireInspectionUpdateData
  ): Promise<FireInspection> {
    const response = await apiClient.put(`${this.baseUrl}/${equipmentId}/inspections/${inspectionId}`, data);
    return response.data.data || response.data;
  }

  async deleteInspection(equipmentId: Id, inspectionId: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${equipmentId}/inspections/${inspectionId}`);
  }
}

export const fireEquipmentService = new FireEquipmentService();
export default fireEquipmentService;
